// src/features/home/components/ApprovalsPanel.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, CircularProgress, IconButton,
  Tooltip, Typography,
} from '@mui/material';
import { TaskAlt as ApprovalsIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { complianceAPI } from '../../compliance/services/compliance';
import HomeCard from './HomeCard';

const TYPE_COLOR = {
  task_completion:      'error.main',
  delegation_signature: 'secondary.main',
};

const TYPE_LABEL_KEY = {
  task_completion:      'home:approvals_panel.types.task_completion',
  delegation_signature: 'home:approvals_panel.types.delegation_signature',
};

function Dot({ type }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 7,
        height: 7,
        borderRadius: '50%',
        bgcolor: TYPE_COLOR[type],
        flexShrink: 0,
        mr: 3,
        ml: 2,
      }}
    />
  );
}

function ApprovalItem({ item, navigate, t }) {
  return (
    <Box
      onClick={() => navigate(item.navigateTo)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1,
        px: 1,
        borderRadius: 1,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Dot type={item.type} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {item.name}
        </Typography>
        {item.subtitle && (
          <Typography variant="caption" color="text.tertiary" noWrap display="block">
            {item.subtitle}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.tertiary" sx={{ ml: 1.5, flexShrink: 0 }}>
        {t(TYPE_LABEL_KEY[item.type])}
      </Typography>
    </Box>
  );
}

/**
 * "Godkendelser" — everything waiting for the viewer to act on. Reviewers see
 * adjusted task completion dates; everyone sees delegations awaiting their
 * signature. The backend decides which of the two the caller gets.
 */
export default function ApprovalsPanel() {
  const { t } = useTranslation(['home']);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await complianceAPI.getApprovalsOverview();
      const raw = res?.data?.data?.items || [];
      setItems(raw.map((item) => ({
        id: item.id,
        type: item.type,
        name: item.name,
        subtitle: item.subtitle,
        navigateTo: item.navigate_to,
      })));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <HomeCard
      icon={<ApprovalsIcon sx={{ fontSize: 20 }} />}
      title={t('home:approvals_panel.title')}
      action={(
        <Tooltip title={t('home:approvals_panel.refresh')}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    >
      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={20} />
        </Box>
      ) : items.length === 0 ? (
        <Typography variant="body2" color="text.tertiary" fontStyle="italic" sx={{ pl: 1, pb: 0.5 }}>
          {t('home:approvals_panel.none')}
        </Typography>
      ) : (
        items.map((item) => (
          <ApprovalItem key={item.id} item={item} navigate={navigate} t={t} />
        ))
      )}
    </HomeCard>
  );
}
